import Axios from 'axios'
import moment from 'moment'
import SocketClient from '@/js/SocketClient'

async function updateCubeConnections({ commit, dispatch, state }) {
  try {
    let getParameters = {
      cubeName: state.cube.name,
    }

    const url = state.urls.backend + '/app/getCubeConnections?' //efToken=' + token
    const result = await Axios.get(url, {
      params: getParameters,
      headers: {
        efToken: state.authentication.backendAccessToken,
      },
    })

    if (!result || !result.data || !result.data.bSuccess || result.data.error) {
      throw new Error(
        `GetCubeConnections request returned with error: ${result.data.error.Message}`
      )
    }
    commit('cubeConnectionsReceived', result.data)
  } catch (err) {
    dispatch('logError', 'Requesting cube connections failed: ' + err)
  }
}

async function loadGlobalCubesList({ commit, dispatch, state }) {
  try {
    let getParameters = {
      ownCubeName: state.cube.name,
      ...state.map.coordinateConstraints,
    }

    const url = state.urls.backend + '/app/getCubeListByCoordinates'
    const result = await Axios.get(url, {
      params: getParameters,
      headers: {
        efToken: state.authentication.backendAccessToken,
      },
    })

    commit('globalCubesListReceived', result.data)
    dispatch('loadProducerPrices')
  } catch (err) {
    dispatch('logError', 'Requesting global cubes list failed: ' + err)
  }
}

async function connectToCube({ commit, state, dispatch }, producer) {
  try {
    commit('setFormState', {
      form: `connectToCube_${producer.cubeName}`,
      state: 'submitting',
    })
    let url = `${state.urls.backend}/app/connectToProducer`
    const result = await Axios.get(url, {
      params: {
        ownCubeName: state.cube.name,
        producerCubeName: producer.cubeName,
      },
      headers: {
        efToken: state.authentication.backendAccessToken,
      },
    })

    if (!result || !result.data || !result.data.bSuccess || result.data.error) {
      if (result.data.message.toLowerCase() === 'connection already present') {
        throw new Error('Connection already present.')
      } else {
        throw new Error(
          `Request failed: ${result.data.error} ${result.message}.`
        )
      }
    }

    producer.params = result.data.data.producers[producer.cubeName].params
    producer.tradingDetails =
      result.data.data.producers[producer.cubeName].tradingDetails
    commit('connectedToProducer', {
      producer,
      unusedEnergyLines: result.data.updatedCube.params.energyLinesAvailable,
    })
    commit('setFormState', {
      form: `connectToCube_${producer.cubeName}`,
      state: 'finished',
    })
  } catch (err) {
    dispatch(
      'logError',
      `Error connecting to producer ${producer.cubeName}: ${err}.`
    )
    commit('setFormState', {
      form: `connectToCube_${producer.cubeName}`,
      state: false,
    })
  }
}

async function disconnectFromCube({ commit, state, dispatch }, producer) {
  try {
    let getParameters = {
      ownCubeName: state.cube.name,
      producerCubeName: producer.cubeName,
    }

    const url = state.urls.backend + '/app/disconnectFromProducer'
    const response = await Axios.get(url, {
      params: getParameters,
      headers: {
        efToken: state.authentication.backendAccessToken,
      },
    })

    commit('disconnectedFromProducer', {
      cubeName: producer.cubeName,
      unusedEnergyLines: response.data.updatedCube.params.energyLinesAvailable,
    })
  } catch (err) {
    dispatch('logError', `Error disconnecting from producer: ${err}.`)
  }
}

async function donateToCube({ commit, state, dispatch }, consumer) {
  try {
    commit('setFormState', {
      form: `donateToCube_${consumer.cubeName}`,
      state: 'submitting',
    })
    let url = `${state.urls.backend}/app/donateToConsumer`
    let resDonate = Axios.get(url, {
      params: {
        producerCubeName: state.cube.name,
        donationRecipient: consumer.cubeName,
      },
      headers: {
        efToken: state.authentication.backendAccessToken,
      },
    })

    let resPrice = dispatch('saveSpecialSellingPriceForConsumer', {
      price: 0,
      consumer: consumer.cubeName,
    })

    const results = await Promise.all([resDonate, resPrice])

    if (
      !results[0] ||
      !results[0].data ||
      !results[0].data.bSuccess ||
      results[0].data.error
    ) {
      throw new Error(
        `Request failed: ${results[0].data.error} ${results[0].message}.`
      )
    }

    consumer.params = results[0].data.data.consumers[consumer.cubeName].params
    consumer.tradingDetails =
      results[0].data.data.consumers[consumer.cubeName].tradingDetails

    commit('donatingToConsumer', {
      consumer,
    })
    commit('cubeConnectionsReceived', results[0].data)

    commit('setFormState', {
      form: `donateToCube_${consumer.cubeName}`,
      state: 'finished',
    })
  } catch (err) {
    dispatch(
      'logError',
      `Error connecting to consumer ${consumer.cubeName}: ${err}.`
    )
    commit('setFormState', {
      form: `donateToCube_${consumer.cubeName}`,
      state: false,
    })
  }
}

async function stopDonatingToCube({ commit, state, dispatch }, consumer) {
  try {
    commit('setFormState', {
      form: `stopDonatingToCube_${consumer.cubeName}`,
      state: 'submitting',
    })
    let url = `${state.urls.backend}/app/disconnectDonationRecipient`
    const resDonate = Axios.get(url, {
      params: {
        producerCubeName: state.cube.name,
        donationRecipient: consumer.cubeName,
      },
      headers: {
        efToken: state.authentication.backendAccessToken,
      },
    })
    const resPrice = dispatch(
      'removeSpecialPriceForConsumer',
      consumer.cubeName
    )
    const results = await Promise.all([resDonate, resPrice])

    if (
      !results[0] ||
      !results[0].data ||
      !results[0].data.bSuccess ||
      results[0].data.error
    ) {
      throw new Error(
        `Disconnect donate request failed: ${results[0].data.error} ${results[0].message}.`
      )
    }

    commit('stopDonatingToConsumer', consumer)
    commit('cubeConnectionsReceived', results[0].data)

    commit('setFormState', {
      form: `stopDonatingToCube_${consumer.cubeName}`,
      state: 'finished',
    })
  } catch (err) {
    dispatch(
      'logError',
      `Error connecting to consumer ${consumer.cubeName}: ${err}.`
    )
    commit('setFormState', {
      form: `stopDonatingToCube_${consumer.cubeName}`,
      state: false,
    })
  }
}

async function loadHistoryData({ commit, state, dispatch }, params = {}) {
  if (!params.startDate) {
    params.startDate = moment().startOf('day')
  }

  if (!params.granularity) {
    params.granularity = '15min'
  }

  try {
    const startTime = moment(params.startDate).format('YYYY-MM-DDTHH:mm')
    const endTime = moment(startTime).add(1, 'days').format('YYYY-MM-DDTHH:mm')

    let getParameters = {
      cubeName: state.cube.name,
      // The parameter is called startTime on the backend even tough it's a date
      // without time because 00:00h is implied.
      startTime: startTime,
      endTime: endTime,
      granularity: params.granularity,
    }

    const url = state.urls.backend + '/app/getReads'

    const promises = []
    promises.push(
      Axios.get(url, {
        params: getParameters,
        headers: {
          efToken: state.authentication.backendAccessToken,
        },
      })
    )

    const statisticsUrl = state.urls.backend + '/app/getTradingStatistics'
    promises.push(
      Axios.get(statisticsUrl, {
        params: {
          cubeName: state.cube.name,
          startTime: startTime,
          endTime: endTime,
        },
        headers: {
          efToken: state.authentication.backendAccessToken,
        },
      })
    )

    const results = await Promise.all(promises)

    if (!results[0] || !results[0].data.bSuccess) {
      throw new Error(results[0].data.message)
    }

    commit('historyDataReceived', {
      ...results[0].data,
      granularity: params.granularity,
    })

    if (!results[1] || !results[1].data.bSuccess) {
      throw new Error(results[1].data.message)
    }

    commit('tradingStatisticsReceived', results[1].data.data)
  } catch (err) {
    dispatch('logError', `Error retrieving history data ${err}.`)
  }
}

async function setupMeterDataConnection({ getters, dispatch }) {
  try {
    const socketUrl = getters.socketUrl
    const client = new SocketClient(socketUrl, this)
    client.connect()
  } catch (err) {
    dispatch('logError', `Error setting up socket connection: ${err}`)
  }
}

async function setConnectionPriority({ state, dispatch, commit }, data) {
  try {
    let priority
    switch (data.priority) {
      case 'low':
        priority = 3
        break

      case 'high':
        priority = 1
        break

      default:
        priority = 2
    }

    let direction, algorithm
    if (data.forConsumers) {
      direction = 'p'
      algorithm = 'producer_priority_trading'
    } else {
      direction = 'c'
      algorithm = 'consumer_priority_trading'
    }

    let params = {
      direction: direction,
      algorithm: algorithm,
      aCubes: [
        {
          cubeName: data.cubeName,
          priority: priority,
        },
      ],
    }

    let url =
      state.urls.backend + '/app/CubeTradingAlgorithmDetails/' + state.cube.name
    await Axios.put(url, params, {
      headers: {
        efToken: state.authentication.backendAccessToken,
      },
    })

    commit('setConnectionPriority', {
      connectionType: data.forConsumers ? 'consumers' : 'producers',
      cubeName: data.cubeName,
      priority: priority,
    })
  } catch (err) {
    dispatch('logError', `Error setting connection priority: ${err}.`)
  }
}

async function restartCube({ state, dispatch }) {
  try {
    let getParameters = {
      cubeName: state.cube.name,
    }

    const url = state.urls.backend + '/app/restartCube'
    const result = await Axios.get(url, {
      params: getParameters,
      headers: {
        efToken: state.authentication.backendAccessToken,
      },
    })
    if (!result || !result.data.bSuccess) {
      throw new Error(result.data.message)
    }
  } catch (err) {
    dispatch('logError', `Error restarting cube ${state.cube.name}: ${err}.`)
  }
}

export default {
  updateCubeConnections,
  loadGlobalCubesList,
  connectToCube,
  disconnectFromCube,
  donateToCube,
  stopDonatingToCube,
  loadHistoryData,
  setupMeterDataConnection,
  setConnectionPriority,
  restartCube,
}
